"use client";

import React, { useState, useEffect } from "react";
import { Phone, Menu, X, ChevronUp, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-row gap-4">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex flex-row gap-4">
        {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        const getHref = (linkItem: any) => {
          if (linkItem.label === "助聽器產品") {
            return "#%e5%8a%a9%e8%81%bd%e5%99%a8%e7%94%a2%e5%93%81";
          }
          return linkItem.href || "#";
        };
        if (hasChildren) {
          return <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuTrigger className="bg-transparent text-base font-medium hover:text-primary transition-colors px-4 shadow-none border-none">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="absolute left-0 top-full mt-1">
                  <ul className="flex flex-col w-[220px] gap-1 p-2">
                    {item.children?.map(child => <li key={child.id}>
                        <NavigationMenuLink asChild>
                          <Link to={getHref(child)} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>;
        }
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={getHref(item)} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-base font-medium hover:text-primary transition-colors px-4 shadow-none border-none")}>
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  closeMenu
}: {
  closeMenu: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const getHref = (linkItem: any) => {
    if (linkItem.label === "助聽器產品") {
      return "#%e5%8a%a9%e8%81%bd%e5%99%a8%e7%94%a2%e5%93%81";
    }
    return linkItem.href || "#";
  };
  const toggleSubMenu = (id: string) => {
    setOpenSubMenu(openSubMenu === id ? null : id);
  };
  if (loading) {
    return <div className="flex flex-col gap-2 p-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-12 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-1 p-4">
      {menuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = openSubMenu === item.id;
      if (hasChildren) {
        return <div key={item.id} data-index={i} className="flex flex-col">
              <Button variant="ghost" onClick={() => toggleSubMenu(item.id)} className="flex items-center justify-between w-full py-4 px-6 text-lg font-medium text-foreground hover:text-primary hover:bg-accent/50 rounded-md transition-colors text-left h-auto">
                {item.label}
                <ChevronDown className={`size-5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </Button>
              {isOpen && <div className="flex flex-col mt-1 mb-2">
                  {item.children?.map(child => <Link key={child.id} to={getHref(child)} className="block py-3 px-6 text-base font-medium text-muted-foreground hover:text-primary transition-colors" onClick={closeMenu}>
                      {child.label}
                    </Link>)}
                </div>}
            </div>;
      }
      return <Link key={item.id} data-index={i} to={getHref(item)} className="block py-4 px-6 text-lg font-medium text-foreground hover:text-primary hover:bg-accent/50 rounded-md transition-colors" onClick={closeMenu}>
            {item.label}
          </Link>;
    })}
    </nav>;
}
export default function NavigationMenuSection() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          setIsMobileMenuOpen(false);
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            setIsMobileMenuOpen(false);
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <section id="導覽選單" className="relative w-full">
      <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm py-3" : "bg-background border-b border-border py-4"}`}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          
          <Link to="/" className="flex-shrink-0 z-10 flex items-center gap-2">
            <img src="https://interton.com.tw/wp-content/uploads/2026/03/cropped-android-chrome-512x512-2.png" className="h-7" alt="瑞聲助聽器到府服務團隊" />
          </Link>

          
          <div className="hidden lg:flex flex-1 justify-center">
            <span className="sr-only">導覽選單</span>
            <MenuProvider menu_id="19">
              <DesktopNavConsumer />
            </MenuProvider>
          </div>

          
          <div className="hidden lg:flex items-center gap-6 flex-shrink-0 z-10">
            <Link to="tel:0800555705" className="flex items-center gap-2 text-secondary font-bold hover:opacity-80 transition-opacity">
              <Phone className="size-5 animate-bounce" />
              <span className="text-lg tracking-wide">0800555705</span>
            </Link>
            
          </div>

          
          <Button variant="ghost" className="lg:hidden z-10 flex items-center gap-1 px-2" onClick={() => setIsMobileMenuOpen(true)} aria-label="開啟選單" aria-expanded={isMobileMenuOpen}>
            <ChevronRight className="size-5" />
            <Menu className="size-6" />
          </Button>
        </div>
      </header>

      
      {isMobileMenuOpen && <div className="fixed inset-0 z-[100] bg-background flex flex-col lg:hidden animate-in fade-in duration-200">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://interton.com.tw/wp-content/uploads/2026/03/cropped-android-chrome-512x512-2.png" className="h-7" alt="瑞聲助聽器到府服務團隊" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="關閉選單">
              <X className="size-6" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="19">
              <MobileNavConsumer closeMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>

          <div className="p-6 border-t border-border bg-muted/30 flex flex-col gap-4">
            <Button asChild variant="outline" size="lg" className="w-full rounded-sm py-6 text-lg shadow-sm">
              <Link to="tel:0800555705" className="flex items-center justify-center gap-2">
                <Phone className="size-6 animate-bounce" />
                <span>0800555705</span>
              </Link>
            </Button>
            
          </div>
        </div>}

      
      {isScrolled && <div className="fixed right-6 top-1/2 -translate-y-1/2 z-[9999]">
          <Button variant="outline" size="icon" className="size-12 md:size-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 bg-background/90 backdrop-blur-sm border-border" onClick={() => window.scrollTo({
        top: 0,
        behavior: "smooth"
      })} aria-label="回到頂部">
            <ChevronUp className="size-6 md:size-7" />
          </Button>
        </div>}

      
    </section>;
}